import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { kindLabel, resultLabel, RESULT_STYLE, MEASURE_STATUS } from "@/lib/inspectionConstants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, FileText, CalendarClock, Loader2, History } from "lucide-react";

export default function AssetHistoryDialog({ open, onClose, asset }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!asset) return;
    setData(null);
    api.get(`/inspections/assets/${asset.id}/history?years=5`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [asset]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const timeline = useMemo(() => {
    if (!data || data.error) return [];
    const ev = [];
    (data.records || []).forEach((r) => ev.push({ kind: "record", date: r.actual_date, ref: r }));
    (data.measures || []).forEach((m) => ev.push({ kind: "measure", date: m.found_date || m.created_at?.slice(0, 10), ref: m }));
    (data.documents || []).forEach((d) => ev.push({ kind: "doc", date: (d.pruef_date || d.created_at || "").slice(0, 10), ref: d }));
    return ev.filter((e) => e.date).sort((a, b) => b.date.localeCompare(a.date));
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><History className="h-5 w-5 text-primary" />Anlagenhistorie (5 Jahre)</DialogTitle></DialogHeader>
        {!data ? <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : data.error ? <div className="p-6 text-center text-slate-400">Historie konnte nicht geladen werden.</div>
          : (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="font-semibold text-slate-900">{asset?.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {[asset?.category, asset?.building, asset?.floor, asset?.room].filter(Boolean).join(" · ")}
                  {asset?.inventory_number ? ` · Inv.-Nr. ${asset.inventory_number}` : ""}{asset?.serial_number ? ` · SN ${asset.serial_number}` : ""}
                </div>
                <div className="text-xs text-primary mt-1">{data.inspections?.length || 0} Prüfungen · {data.records?.length || 0} Abschlüsse · {data.documents?.length || 0} Dokumente · {data.measures?.length || 0} Mängel</div>
              </div>

              {timeline.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">Noch keine Vorgänge für diese Anlage.</div>
                : (
                  <div className="relative pl-5 border-l-2 border-slate-100 space-y-3" data-testid="asset-timeline">
                    {timeline.map((e, i) => {
                      if (e.kind === "record") {
                        const r = e.ref;
                        return (
                          <div key={`r${i}`} className="relative" data-testid={`hist-record-${r.id}`}>
                            <span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full bg-green-500 border-2 border-white" />
                            <div className="flex items-center gap-2 flex-wrap">
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                              <span className="text-sm font-medium text-slate-800">{r.title}</span>
                              <span className="text-xs font-mono-tech text-slate-500">{r.actual_date}</span>
                              <Badge variant="outline" className={`${RESULT_STYLE[r.result] || ""} text-[10px]`}>{resultLabel(r.result)}</Badge>
                            </div>
                            <div className="text-xs text-slate-500 ml-5">{kindLabel(r.kind)}{r.performed_by ? ` · ${r.performed_by}` : ""}{r.defects_found ? ` · Mängel: ${r.defects_found}` : ""}</div>
                          </div>
                        );
                      }
                      if (e.kind === "measure") {
                        const m = e.ref; const st = MEASURE_STATUS[m.status] || {};
                        return (
                          <div key={`m${i}`} className="relative" data-testid={`hist-measure-${m.id}`}>
                            <span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full bg-red-500 border-2 border-white" />
                            <div className="flex items-center gap-2 flex-wrap">
                              <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                              <span className="text-sm font-medium text-slate-800">{m.title}</span>
                              <span className="text-xs font-mono-tech text-slate-500">{e.date}</span>
                              <Badge variant="outline" className={`${st.bg || ""} ${st.text || ""} ${st.border || ""} text-[10px]`}>{st.label || m.status}</Badge>
                            </div>
                            {m.required_action && <div className="text-xs text-slate-500 ml-5">{m.required_action}</div>}
                          </div>
                        );
                      }
                      const d = e.ref;
                      return (
                        <div key={`d${i}`} className="relative" data-testid={`hist-doc-${d.id}`}>
                          <span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full bg-blue-500 border-2 border-white" />
                          <div className="flex items-center gap-2 flex-wrap">
                            <FileText className="h-3.5 w-3.5 text-blue-600" />
                            <span className="text-sm text-slate-800">{d.name}</span>
                            <span className="text-xs font-mono-tech text-slate-500">{e.date}</span>
                            <Badge variant="outline" className="text-[10px]">v{d.version}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
